import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { BackNav } from "@/components/BackNav";
import { CenteredError, Spinner } from "@/components/Spinner";
import { useCategories, useSetCategories } from "@/api/hooks";
import { SpheresPicker } from "@/components/SpheresPicker";
import { StickyAction } from "@/components/StickyAction";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";

/**
 * Категории — множественный выбор сфер, на которые юзер хочет получать вакансии.
 * Сохранение происходит при тапе на «Зберегти» (не на каждый toggle, чтобы
 * не дёргать API на каждый клик).
 */
export function CategoriesPage() {
  const query = useCategories();
  const mutation = useSetCategories();

  const initialIds = useMemo(() => new Set(query.data?.selected_ids ?? []), [query.data]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Когда данные пришли — инициализируем выбранное
  useEffect(() => {
    if (query.data) setSelected(new Set(query.data.selected_ids));
  }, [query.data]);

  if (query.isLoading) return <Spinner />;
  if (query.isError) return <CenteredError message={query.error.message} />;

  const categories = query.data!.items;
  const dirty =
    selected.size !== initialIds.size ||
    [...selected].some((id) => !initialIds.has(id));

  const toggle = (id: number) => {
    tg()?.HapticFeedback.selectionChanged();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    try {
      await mutation.mutateAsync({ category_ids: [...selected] });
      tg()?.HapticFeedback.notificationOccurred("success");
    } catch (e) {
      tg()?.HapticFeedback.notificationOccurred("error");
      showAppAlert(`Не вдалося зберегти: ${(e as Error).message}`);
    }
  };

  return (
    <>
      <BackNav />
      <PageHeader
        eyebrow="налаштування"
        title="Твої сфери"
        titleLead="Твої"
        titleAccent="сфери."
        scribbleWidth={150}
        subtitle="Обери напрями, у яких шукаєш замовлення — вакансії по них приходитимуть у бот одразу після публікації."
      />

      <SpheresPicker
        items={categories}
        selected={selected}
        onToggle={toggle}
        onSelectAll={() => setSelected(new Set(categories.map((c) => c.id)))}
        onClear={() => setSelected(new Set())}
      />

      <StickyAction
        label="Зберегти сфери"
        pending={mutation.isPending}
        disabled={!dirty}
        onClick={save}
      />
    </>
  );
}
